import React from 'react';
import PropTypes from 'prop-types';
import {
  ButtonCheck,
  Modal,
  ModalBody,
  ModalHeader,
  ButtonIcon,
  ModalFooter,
  Button,
} from '@statsbomb/kitbag-components';
import { isEmpty, uniq } from 'lodash';
import {
  PositionGroup,
  PositionCategory,
  PositionModalContent,
  PositionModalBigWrapper,
  PositionModalHeader,
} from './PositionFilter.styles';
import PositionIcon from '../PositionIcon/PositionIcon';
import { POSITION_ICON_SIZES } from '../PositionIcon/PositionIcon.constants';
import { ALL_POSITION_CODES } from './PositionFilter.constants';
import { POSITION_CATEGORIES } from '../../utils/constants/positions';

const PositionModal = ({
  isOpen = false,
  positionDefinitions,
  setDialogPositions,
  onConfirm,
  onClose,
  asColumn = false,
  isRosterPositions = true,
}) => {
  const resetPositions = () => {
    const selectedPositions = positionDefinitions
      .filter((p) => p.isSelected)
      .map((p) => p.apiCode);
    setDialogPositions(selectedPositions);
  };

  const dialogPositions = positionDefinitions
    .filter((p) => p.isSelectedDialog)
    .map((p) => p.apiCode);
  // disable the confirm button if nothing is selected
  const isDisabled = isEmpty(dialogPositions);

  const onCategoryClick = (categoryKey, isChecked) => {
    const catPositions = positionDefinitions
      .filter((p) => p.positionCategory === categoryKey)
      .map((p) => p.apiCode);
    if (isChecked) {
      const newSelection = uniq(dialogPositions.concat(catPositions));
      setDialogPositions(newSelection);
    } else {
      const updatedSelection = dialogPositions.filter(
        (d) => !catPositions.includes(d)
      );
      setDialogPositions(updatedSelection);
    }
  };

  const onPositionClick = (positionApiCode, isChecked) => {
    if (isChecked) {
      const newSelection = dialogPositions.concat(positionApiCode);
      setDialogPositions(newSelection);
    } else {
      const updatedSelection = dialogPositions.filter(
        (d) => d !== positionApiCode
      );
      setDialogPositions(updatedSelection);
    }
  };

  const positionCategories = Object.values(POSITION_CATEGORIES).map((cat) => {
    const catPositions = positionDefinitions.filter(
      (p) => p.positionCategory === cat.key
    );
    const allSelected = catPositions.every((p) => p.isSelectedDialog);
    const someSelected = catPositions.some((p) => p.isSelectedDialog);
    return { ...cat, positions: catPositions, allSelected, someSelected };
  });

  const allPositionsSelected = positionDefinitions.every(
    (p) => p.isSelectedDialog
  );
  const somePositionsSelected = positionDefinitions.some(
    (p) => p.isSelectedDialog
  );

  const onAllNoneClick = () => {
    if (allPositionsSelected) {
      setDialogPositions([]);
    } else {
      const updatedSelection = positionDefinitions.map((p) => p.apiCode);
      setDialogPositions(updatedSelection);
    }
  };

  const getPositionIcon = (position) => {
    if (isRosterPositions) {
      return (
        <PositionIcon
          rosterPosition={position.apiCode}
          size={POSITION_ICON_SIZES.SMALL}
        />
      );
    }
    return (
      <PositionIcon
        alignmentPosition={position.apiCode}
        size={POSITION_ICON_SIZES.SMALL}
      />
    );
  };

  return (
    <Modal
      id="select-positions-modal"
      isOpen={isOpen}
      onCancel={() => resetPositions()}
      onConfirm={() => onConfirm(dialogPositions)}
      onEscape={() => onConfirm(dialogPositions)}
      testId="select-positions-modal"
    >
      <ModalHeader>
        <PositionModalHeader>
          <div>
            <div>Positions</div>
            <div className="buttons">
              <ButtonIcon
                onClick={onClose}
                variant="ghost"
                shape="pill"
                size="small"
                icon="Close"
                title="Cancel"
                testId="close-positions-modal"
              />
            </div>
          </div>
        </PositionModalHeader>
      </ModalHeader>
      <ModalBody>
        <PositionModalBigWrapper>
          <ButtonCheck
            id="all-or-none"
            testId="all-or-none-button"
            label={allPositionsSelected ? 'Remove all' : 'Add all'}
            onChange={onAllNoneClick}
            checked={allPositionsSelected}
            indeterminate={somePositionsSelected}
          />
        </PositionModalBigWrapper>
        <PositionModalContent $asColumn={asColumn}>
          {positionCategories.map((category) => {
            const categoryId = `${category.key}-button`;
            const isOffense = category.key === POSITION_CATEGORIES.OFFENSE.key;

            return (
              <PositionCategory key={categoryId}>
                <ButtonCheck
                  id={categoryId}
                  label={category.name}
                  onChange={(e) =>
                    onCategoryClick(category.key, e.target.checked)
                  }
                  checked={category.allSelected}
                  indeterminate={category.someSelected}
                />
                <PositionGroup $wider={isOffense}>
                  {category.positions.map((position) => (
                    <ButtonCheck
                      id={`${categoryId}-${position.apiCode}`}
                      key={`${categoryId}-${position.apiCode}`}
                      testId={`${categoryId}-${position.apiCode}`}
                      label={position.label}
                      title={position.tooltip} // KB doesn't have this attribute yet but assume it will soon
                      onChange={(e) =>
                        onPositionClick(position.apiCode, e.target.checked)
                      }
                      checked={position.isSelectedDialog}
                      inline={false}
                      customCheckedIcon={getPositionIcon(position)}
                      size="small"
                    />
                  ))}
                </PositionGroup>
              </PositionCategory>
            );
          })}
        </PositionModalContent>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="ghost"
          onClick={onClose}
          testId="cancel-positions-modal-button"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => onConfirm(dialogPositions)}
          isDisabled={isDisabled}
          testId="save-positions-modal-button"
        >
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

PositionModal.propTypes = {
  // whether the dialog is open or not
  isOpen: PropTypes.bool,
  // the positional stats and whether they're currently selected
  positionDefinitions: PropTypes.arrayOf(
    PropTypes.shape({
      apiCode: PropTypes.oneOf(ALL_POSITION_CODES),
      positionCategory: PropTypes.oneOf(Object.keys(POSITION_CATEGORIES)),
      label: PropTypes.string,
      tooltip: PropTypes.string,
      isSelected: PropTypes.bool,
      isSelectedDialog: PropTypes.bool,
    })
  ).isRequired,
  // update the dialog positions
  setDialogPositions: PropTypes.func.isRequired,
  // callback for the confirm button
  onConfirm: PropTypes.func.isRequired,
  // callback for closing the modal without confirming
  onClose: PropTypes.func.isRequired,
  // render as single column layout or horizontal
  asColumn: PropTypes.bool,
  // whether roster or alignment positions are being handled (affects button icons)
  isRosterPositions: PropTypes.bool,
};

export default PositionModal;
